import { Injectable, inject, signal } from '@angular/core';
import type { ChartCardConfig } from '../models/chart-config.model';
import type { TableCardConfig } from '../models/table-config.model';
import type { TableData } from '../models/chart-data.model';
import type { DateRangeExpression } from '../models/date-range-expression.model';
import { CardConfigChangeService } from './card-config-change.service';
import { isTableCardConfig } from './card-config-change.types';
import { CardStateService } from '../states/card-state.service';
import { TablePreviewDataLoader } from '../components/editors/jepto-table-editor/services/table-preview-data-loader.service';
import {
	buildGetCardDataTimeDimensionInput,
	resolveChartEditorPreviewDateRangeExpression,
} from '../utils/chart-card-date-range.util';
import type {
	TableGridQueryPatch,
	TablePagingParams,
	TableSortParams,
} from '../models/table-grid-query.types';
import { tablePageSizeFromConfig } from '../utils/table-presentation-visualization.util';
import { resolveTableOrderMemberKey } from '../utils/table-column.util';
import { warn } from '@jepto/shared/utils/logger';

export type { TableGridQueryPatch } from '../models/table-grid-query.types';

/**
 * True when runtime paging/sort patch would not change in-memory table `data`.
 * Prevents fetchCallback → getCardData → fingerprint → applyQuery loops.
 */
function effectiveOrderAfterPatch(
	currentOrder: Record<string, unknown>,
	orderPatch: Record<string, unknown>
): Record<string, unknown> {
	const result: Record<string, unknown> = { ...currentOrder };
	for (const [key, value] of Object.entries(orderPatch)) {
		if (value === null) {
			delete result[key];
		} else {
			result[key] = value;
		}
	}
	return result;
}

function tableGridQueryAlreadyApplied(
	config: TableCardConfig,
	patchData: Record<string, unknown>,
	includesSort: boolean
): boolean {
	const currentOffset = Math.max(0, Number(config.data?.offset ?? 0));
	const currentLimit = Math.max(
		1,
		Number(config.data?.limit ?? tablePageSizeFromConfig(config))
	);
	const nextOffset = Math.max(0, Number(patchData['offset'] ?? 0));
	const nextLimit = Math.max(1, Number(patchData['limit'] ?? currentLimit));
	if (currentOffset !== nextOffset || currentLimit !== nextLimit) {
		return false;
	}
	if (!includesSort) {
		return true;
	}
	const currentOrder = (config.data?.order ?? {}) as Record<string, unknown>;
	const orderPatch = (patchData['order'] ?? {}) as Record<string, unknown>;
	const nextOrder = effectiveOrderAfterPatch(currentOrder, orderPatch);
	const currentKeys = Object.keys(currentOrder);
	const nextKeys = Object.keys(nextOrder);
	if (currentKeys.length !== nextKeys.length) {
		return false;
	}
	return nextKeys.every(key => currentOrder[key] === nextOrder[key]);
}

/**
 * Patches in-memory table `data` and refetches via `getCardData` for Grid `fetchCallback`.
 * Grid paging/sort are runtime-only (no GraphQL `updateCard` / autosave). Runtime Grid
 * sort is sent as `dataConfig.order` on `getCardData` with `pagination`; saved card
 * order alone is not re-sent on paging (backend already has it).
 */
@Injectable({
	providedIn: 'root',
})
export class TableCardInteractionService {
	private readonly changeService = inject(CardConfigChangeService);
	private readonly cardState = inject(CardStateService);
	private readonly previewLoader = inject(TablePreviewDataLoader);

	/**
	 * Card ids whose latest `getCardData` was kicked by Grid paging/sort.
	 * Table card must not call `provider.applyQuery()` for those payloads — that
	 * aborts the in-flight `fetchCallback` and Highcharts treats the empty chunk
	 * as `totalRowCount: 0` ("Showing 0–0 of 0").
	 */
	readonly #gridPagingRefetchCards = new Set<string>();
	/** Nested mark/clear while concurrent fetchCallbacks share one wait. */
	readonly #gridPagingRefetchDepth = new Map<string, number>();
	/**
	 * Coalesce concurrent `applyGridQueryAndWait` for the same card slice.
	 * Without this, each Grid retry cancels the previous `getCardData` and the
	 * earlier wait clears the paging lock → applyQuery → "Showing 0–0 of 0".
	 */
	readonly #inFlightSliceWait = new Map<string, Promise<TableData>>();
	/**
	 * Cards whose Grid sort set a runtime `data.order` override for `getCardData`
	 * `dataConfig.order`. Saved card order alone must not be sent on paging — that
	 * caused public-viewer fetchAndWait timeouts (GraphQL ack, no matching WS slice).
	 */
	readonly #runtimeOrderOverrideCards = new Set<string>();

	/**
	 * Active page / report date expression for global-default table cards.
	 * Hosts (report editor, shared viewer) sync this so Grid paging `getCardData`
	 * matches report settings instead of falling back to last 30 days.
	 */
	readonly #pageDateRangeExpression = signal<
		DateRangeExpression | undefined
	>(undefined);

	/** Sync the page/report date range used when refetching global-default tables. */
	setPageDateRangeExpression(
		expression: DateRangeExpression | undefined
	): void {
		this.#pageDateRangeExpression.set(expression);
	}

	/** True when any slice wait is in flight for this card (any offset/limit). */
	hasInFlightSliceWait(cardId: string): boolean {
		const prefix = `${cardId}|`;
		for (const key of this.#inFlightSliceWait.keys()) {
			if (key.startsWith(prefix)) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Await the in-flight slice wait for this card, if any (any offset).
	 * Used so a competing Grid `fetchCallback` does not start a second getCardData.
	 */
	// fallow-ignore-next-line unused-class-member
	async awaitInFlightSliceWait(cardId: string): Promise<TableData | null> {
		const prefix = `${cardId}|`;
		for (const [key, wait] of this.#inFlightSliceWait) {
			if (key.startsWith(prefix)) {
				return wait;
			}
		}
		return null;
	}

	/**
	 * Drop in-flight Grid paging waits for this card (create-add-card: editor
	 * preview paged, then the canvas grid fetches page 1 on the same cardId).
	 * Also cancels the shared preview loader wait so a later page-1 fetchAndWait
	 * cannot stamp an in-flight page-2 payload as offset 0.
	 */
	abandonPagingWaits(cardId: string): void {
		const prefix = `${cardId}|`;
		for (const key of [...this.#inFlightSliceWait.keys()]) {
			if (key.startsWith(prefix)) {
				this.#inFlightSliceWait.delete(key);
			}
		}
		this.#gridPagingRefetchDepth.delete(cardId);
		this.#gridPagingRefetchCards.delete(cardId);
		if (this._pagingLoadingCardIds().has(cardId)) {
			const next = new Set(this._pagingLoadingCardIds());
			next.delete(cardId);
			this._pagingLoadingCardIds.set(next);
		}
		this.previewLoader.cancelCard(cardId);
	}

	/** Drives the report-canvas / card top progress bar during Grid paging. */
	private readonly _pagingLoadingCardIds = signal<ReadonlySet<string>>(
		new Set()
	);
	// fallow-ignore-next-line unused-class-member
	readonly pagingLoadingCardIds = this._pagingLoadingCardIds.asReadonly();

	/** True while a Grid-driven paging/sort refetch is in flight for this card. */
	shouldSkipApplyQueryForPaging(cardId: string): boolean {
		return this.#gridPagingRefetchCards.has(cardId);
	}

	isPagingLoading(cardId: string): boolean {
		return this._pagingLoadingCardIds().has(cardId);
	}

	/**
	 * Waits until Grid-driven slice fetches and paging locks clear for this card.
	 * Used before destroying a Grid instance so Highcharts does not apply chunks
	 * or pagination updates after `Grid.destroy()`.
	 */
	async awaitGridRemoteIdle(
		cardId: string,
		timeoutMs = 8000
	): Promise<void> {
		const trimmed = cardId.trim();
		if (!trimmed) {
			return;
		}
		const started = Date.now();
		for (;;) {
			if (
				!this.hasInFlightSliceWait(trimmed) &&
				!this.isPagingLoading(trimmed)
			) {
				await new Promise<void>(resolve =>
					requestAnimationFrame(() =>
						requestAnimationFrame(() => resolve())
					)
				);
				if (
					!this.hasInFlightSliceWait(trimmed) &&
					!this.isPagingLoading(trimmed)
				) {
					return;
				}
			}
			if (Date.now() - started >= timeoutMs) {
				warn(
					'[TableCardInteraction] awaitGridRemoteIdle timed out; proceeding without abandoning paging waits',
					{ cardId: trimmed }
				);
				return;
			}
			await new Promise(resolve => setTimeout(resolve, 16));
		}
	}

	markGridPagingRefetch(cardId: string): void {
		const depth = (this.#gridPagingRefetchDepth.get(cardId) ?? 0) + 1;
		this.#gridPagingRefetchDepth.set(cardId, depth);
		this.#gridPagingRefetchCards.add(cardId);
		if (!this._pagingLoadingCardIds().has(cardId)) {
			const next = new Set(this._pagingLoadingCardIds());
			next.add(cardId);
			this._pagingLoadingCardIds.set(next);
		}
	}

	clearGridPagingRefetch(cardId: string): void {
		const depth = (this.#gridPagingRefetchDepth.get(cardId) ?? 0) - 1;
		if (depth > 0) {
			this.#gridPagingRefetchDepth.set(cardId, depth);
			return;
		}
		this.#gridPagingRefetchDepth.delete(cardId);
		this.#gridPagingRefetchCards.delete(cardId);
		if (this._pagingLoadingCardIds().has(cardId)) {
			const next = new Set(this._pagingLoadingCardIds());
			next.delete(cardId);
			this._pagingLoadingCardIds.set(next);
		}
	}

	applyPageChange(cardId: string, event: TablePagingParams): TableCardConfig {
		return this.applyGridQuery(cardId, { paging: event });
	}

	applySortChange(cardId: string, event: TableSortParams): TableCardConfig {
		return this.applyGridQuery(cardId, { sort: event });
	}

	/** One config patch + one `getCardData` for Grid-driven paging and/or sort. */
	applyGridQuery(
		cardId: string,
		query: TableGridQueryPatch
	): TableCardConfig {
		const prepared = this.#prepareGridQuery(cardId, query);
		if (!prepared) {
			return this.cardState.getCard(cardId) as TableCardConfig;
		}
		if (prepared.alreadyApplied) {
			return prepared.config;
		}
		// Do not let a sync applyQuery-driven refetch cancel an in-flight
		// fetchCallback wait for another page on the same card.
		if (this.hasInFlightSliceWait(cardId)) {
			return prepared.config;
		}
		this.#commitGridQuery(cardId, prepared.config);
		void this.#refetch(cardId, prepared.config);
		return prepared.config;
	}

	/**
	 * Patch runtime paging/sort and wait for the matching `getCardData` / WS slice.
	 * Used by {@link TableRemoteFetchBridge.fetchSlice}.
	 */
	async applyGridQueryAndWait(
		cardId: string,
		query: TableGridQueryPatch
	): Promise<TableData> {
		const prepared = this.#prepareGridQuery(cardId, query);
		if (!prepared) {
			throw new Error(
				`[TableCardInteractionService] No table card in CardState for "${cardId}"`
			);
		}
		const flightKey = this.#sliceWaitKey(cardId, prepared.config);
		const inFlight = this.#inFlightSliceWait.get(flightKey);
		if (inFlight) {
			return inFlight;
		}
		// Grid often re-enters fetchCallback for page 1 while page 2 is loading.
		// Starting that getCardData cancels the in-flight page-2 wait (same cardId).
		if (this.hasInFlightSliceWait(cardId)) {
			throw new Error(
				`[TableCardInteractionService] Paging already in flight for "${cardId}"`
			);
		}

		let resolveWait!: (value: TableData) => void;
		let rejectWait!: (reason: unknown) => void;
		const wait = new Promise<TableData>((resolve, reject) => {
			resolveWait = resolve;
			rejectWait = reject;
		});
		// Reserve before any await so a competing fetchCallback sees in-flight.
		this.#inFlightSliceWait.set(flightKey, wait);

		try {
			if (!prepared.alreadyApplied) {
				this.#commitGridQuery(cardId, prepared.config);
			} else {
				this.markGridPagingRefetch(cardId);
			}
			const result = await this.#refetchAndWait(cardId, prepared.config);
			resolveWait(result);
			return result;
		} catch (error) {
			rejectWait(error);
			throw error;
		} finally {
			// Defer unlock so the table-card fingerprint effect can observe the
			// new payload while shouldSkipApplyQuery is still true. Clearing
			// synchronously lets applyQuery re-enter fetchCallback(offset=0)
			// and overwrite the page we just loaded.
			setTimeout(() => {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						if (this.#inFlightSliceWait.get(flightKey) === wait) {
							this.#inFlightSliceWait.delete(flightKey);
							this.clearGridPagingRefetch(cardId);
						}
					});
				});
			}, 0);
		}
	}

	#sliceWaitKey(cardId: string, config: TableCardConfig): string {
		const offset = Math.max(0, Number(config.data?.offset ?? 0));
		const limit = Math.max(
			1,
			Number(config.data?.limit ?? tablePageSizeFromConfig(config))
		);
		const order = config.data?.order ?? {};
		const orderKey = Object.keys(order)
			.sort()
			.map(
				key =>
					`${key}:${String((order as Record<string, unknown>)[key])}`
			)
			.join(',');
		return `${cardId}|${offset}|${limit}|${orderKey}`;
	}

	#prepareGridQuery(
		cardId: string,
		query: TableGridQueryPatch
	): {
		config: TableCardConfig;
		alreadyApplied: boolean;
		includesSort: boolean;
		patchData: Record<string, unknown>;
	} | null {
		const current = this.cardState.getCard(cardId);
		if (!current || !isTableCardConfig(current)) {
			return null;
		}

		const patchData: Record<string, unknown> = {};
		const sort = query.sort;
		const columnRaw = sort?.column?.trim();
		// Sort patch present when applySortChange / Grid sort path runs (including clear).
		const includesSort = query.sort !== undefined;

		if (includesSort) {
			const memberKey = columnRaw
				? resolveTableOrderMemberKey(current, columnRaw)
				: '';
			const direction = sort?.direction;
			// Deep-merge on `data.order` keeps stale Cube keys unless we null them out.
			const orderPatch: Record<string, 'asc' | 'desc' | null> = {};
			for (const key of Object.keys(current.data?.order ?? {})) {
				orderPatch[key] = null;
			}
			if (memberKey && (direction === 'asc' || direction === 'desc')) {
				orderPatch[memberKey] = direction;
				this.#runtimeOrderOverrideCards.add(cardId);
			} else {
				this.#runtimeOrderOverrideCards.delete(cardId);
			}
			patchData['order'] = orderPatch;
			const pageSize = tablePageSizeFromConfig(current);
			patchData['offset'] = 0;
			patchData['limit'] = pageSize;
		} else if (query.paging) {
			const pageSize = query.paging.pageSize;
			patchData['limit'] = pageSize;
			patchData['offset'] = Math.max(
				0,
				(query.paging.page - 1) * pageSize
			);
		} else {
			return null;
		}

		if (tableGridQueryAlreadyApplied(current, patchData, includesSort)) {
			return {
				config: current,
				alreadyApplied: true,
				includesSort,
				patchData,
			};
		}

		const result = this.changeService.applyChange(cardId, current, {
			type: 'patch',
			origin: 'runtime',
			patch: { data: patchData },
		});
		const updated = result.config;
		if (!isTableCardConfig(updated)) {
			return null;
		}
		return {
			config: updated,
			alreadyApplied: false,
			includesSort,
			patchData,
		};
	}

	#commitGridQuery(cardId: string, updated: TableCardConfig): void {
		// Mark before CardState write so config-driven grid effects skip applyQuery.
		this.markGridPagingRefetch(cardId);
		// Omit requestId so CardStateService does not drop the patch when the
		// card's requestId is unchanged (ULID compare <= 0). Runtime offset/limit
		// must stick or fetchCallback → getCardData loops forever.
		const { requestId: _requestId, ...configForState } =
			updated as TableCardConfig & {
				requestId?: string;
			};
		this.cardState.updateCard(cardId, configForState);
	}

	async #refetch(cardId: string, config: TableCardConfig): Promise<void> {
		try {
			await this.#refetchAndWait(cardId, config);
		} catch {
			// finally in #refetchAndWait already cleared the paging lock
		}
	}

	async #refetchAndWait(
		cardId: string,
		config: TableCardConfig
	): Promise<TableData> {
		// Caller already marked via #commitGridQuery / alreadyApplied branch.
		// Do not clear the paging lock here when called from applyGridQueryAndWait —
		// that path defers clear so the fingerprint effect can skip applyQuery.
		const deferLockClear = this.hasInFlightSliceWait(cardId);
		try {
			const asChart = config as unknown as ChartCardConfig;
			const expression = resolveChartEditorPreviewDateRangeExpression(
				asChart,
				this.#pageDateRangeExpression()
			);
			const tdInput = buildGetCardDataTimeDimensionInput(expression);
			const offset = Math.max(0, Number(config.data?.offset ?? 0));
			const limit = Math.max(
				1,
				Number(config.data?.limit ?? tablePageSizeFromConfig(config))
			);
			const order = config.data?.order as
				| Record<string, 'asc' | 'desc'>
				| undefined;
			const orderForFetch =
				this.#runtimeOrderOverrideCards.has(cardId) &&
				order &&
				Object.keys(order).length > 0
					? order
					: undefined;

			return await this.previewLoader.fetchAndWait(
				cardId,
				tdInput,
				config,
				{
					pagination: { offset, limit },
					...(orderForFetch ? { order: orderForFetch } : {}),
				}
			);
		} finally {
			if (!deferLockClear) {
				this.clearGridPagingRefetch(cardId);
			}
		}
	}
}
